import type { FormField, FormFieldType } from '@kanbu/schema/contracts';
import { useLingui, Trans } from '@lingui/react/macro';
import { Button, Input, Label, Select, SelectItem, Switch } from '@utima/ui';
import { ChevronDown, ChevronUp, Copy, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { FORM_FIELD_TYPES, slugifyFieldName } from '../lib/formFieldDefaults';

export interface FormFieldRowProps {
  field: FormField;
  fieldKeyLocked?: boolean;
  index: number;
  total: number;
  onChange: (field: FormField) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

export function FormFieldRow({
  field,
  fieldKeyLocked = false,
  index,
  total,
  onChange,
  onRemove,
  onDuplicate,
  onMoveUp,
  onMoveDown,
}: FormFieldRowProps) {
  const { t } = useLingui();
  const [autoKey, setAutoKey] = useState(!fieldKeyLocked && !field.label);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const update = (patch: Partial<FormField>) => {
    const next = { ...field, ...patch };

    if (
      autoKey &&
      !fieldKeyLocked &&
      patch.label !== undefined &&
      patch.name === undefined
    ) {
      next.name = slugifyFieldName(patch.label);
    }

    if (patch.type && patch.type !== 'select') {
      next.options = undefined;
    }

    onChange(next);
  };

  const optionsText = (field.options ?? []).join(', ');

  return (
    <div className='rounded-radius border border-border p-4'>
      <div className='mb-3 flex items-center justify-between gap-2'>
        <span className='text-sm font-medium text-muted-fg'>
          <Trans>Field {index + 1}</Trans>
        </span>
        <div className='flex items-center gap-1'>
          <Button
            size='sm'
            variant='ghost'
            disabled={index === 0}
            onClick={onMoveUp}
            title={t`Move up`}
          >
            <ChevronUp className='size-4' />
          </Button>
          <Button
            size='sm'
            variant='ghost'
            disabled={index >= total - 1}
            onClick={onMoveDown}
            title={t`Move down`}
          >
            <ChevronDown className='size-4' />
          </Button>
          <Button
            size='sm'
            variant='ghost'
            onClick={onDuplicate}
            title={t`Duplicate`}
          >
            <Copy className='size-4' />
          </Button>
          <Button
            size='sm'
            variant='ghost'
            onClick={onRemove}
            title={t`Remove`}
          >
            <Trash2 className='size-4 text-danger' />
          </Button>
        </div>
      </div>

      <div className='grid gap-3 md:grid-cols-2'>
        <div className='space-y-1.5'>
          <Label>
            <Trans>Label</Trans>
          </Label>
          <Input
            value={field.label}
            onChange={event => update({ label: event.target.value })}
            placeholder={t`e.g. Email address`}
          />
        </div>

        <div className='space-y-1.5'>
          <Label>
            <Trans>Field key</Trans>
          </Label>
          <Input
            value={field.name}
            readOnly={fieldKeyLocked}
            onChange={event => {
              if (fieldKeyLocked) return;
              setAutoKey(false);
              update({ name: event.target.value });
            }}
            placeholder={t`e.g. email`}
          />
        </div>

        <div className='space-y-1.5'>
          <Label>
            <Trans>Type</Trans>
          </Label>
          <Select
            value={field.type}
            onValueChange={value => update({ type: value as FormFieldType })}
          >
            {FORM_FIELD_TYPES.map(type => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </Select>
        </div>

        <div className='flex items-end gap-2 pb-1'>
          <Switch
            checked={field.required}
            onCheckedChange={checked => update({ required: checked })}
          />
          <Label>
            <Trans>Required</Trans>
          </Label>
        </div>

        <div className='space-y-1.5 md:col-span-2'>
          <Label>
            <Trans>Placeholder</Trans>
          </Label>
          <Input
            value={field.placeholder ?? ''}
            onChange={event =>
              update({ placeholder: event.target.value || undefined })
            }
          />
        </div>

        {field.type === 'select' ? (
          <div className='space-y-1.5 md:col-span-2'>
            <Label>
              <Trans>Options (comma-separated)</Trans>
            </Label>
            <Input
              value={optionsText}
              onChange={event =>
                update({
                  options: event.target.value
                    .split(',')
                    .map(option => option.trim())
                    .filter(Boolean),
                })
              }
              placeholder={t`Option A, Option B, Option C`}
            />
          </div>
        ) : null}
      </div>

      <button
        type='button'
        className='mt-3 text-xs text-primary hover:underline'
        onClick={() => setShowAdvanced(value => !value)}
      >
        {showAdvanced ? (
          <Trans>Hide advanced</Trans>
        ) : (
          <Trans>Advanced validation</Trans>
        )}
      </button>

      {showAdvanced ? (
        <div className='mt-2 space-y-1.5'>
          <Label>
            <Trans>Regex pattern</Trans>
          </Label>
          <Input
            value={field.validation ?? ''}
            onChange={event =>
              update({ validation: event.target.value || undefined })
            }
            placeholder={t`Optional regular expression`}
          />
        </div>
      ) : null}
    </div>
  );
}
